import { useRouter } from "next/router";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getOrg, renameOrg } from "pages/api-clients/orgs";
import { useQueryToasterErrors } from "./useToaster";

export function useOrg():
  | { loading: true; id: undefined; isFetched: boolean }
  | ({ loading: false; isFetched: boolean } & Org) {
  const orgId = `${useRouter().query.orgId ?? ""}`.trim() || undefined;
  const query = useQuery(["org", orgId], () => getOrg(orgId!), {
    enabled: Boolean(orgId),
  });
  useQueryToasterErrors(query);
  const isFetched = query.isFetched;
  if (query.isLoading || !query.data?.id)
    return { loading: true, id: undefined, isFetched };
  return { loading: false, ...query.data, isFetched };
}

export type Org = Exclude<
  ReturnType<typeof getOrg> extends Promise<infer T> ? T : never,
  undefined
>;

export function useOrgNameMutator(
  props: Partial<{
    onSuccess(): void;
    onError(): void;
  }>
) {
  const client = useQueryClient();
  const org = useOrg();
  return useMutation({
    mutationFn: async (name: string) => renameOrg(org.id!, name),
    onSuccess: async () => {
      client.invalidateQueries({ queryKey: ["org", org.id!] });
      props.onSuccess && props.onSuccess();
    },
    onError: async () => {
      props.onError && props.onError();
    },
  });
}
